import React, { useState, useEffect } from 'react';
import { X, Save, Plus, Trash2 } from 'lucide-react';
import { useUnidadesMedida } from '../hooks/useUnidadesMedida';
import type {
  Ingrediente,
  UnidadMedida,
  Proveedor,
  IngredienteProveedor,
} from '../types/ingredientes';
import { useProveedores } from '../hooks/useProveedores';
import { useCategorias, useCreateCategoria } from '../hooks/useCategorias';

// Helper para capitalizar marca
const capitalizarMarca = (marca: string): string => {
  if (!marca || marca.trim() === '') return 'Genérica';
  const trimmed = marca.trim().toLowerCase();
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
};

interface IngredienteFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (ingrediente: Partial<Ingrediente>) => void;
  ingrediente?: Ingrediente | null;
  modo: 'crear' | 'editar';
}

interface FormData {
  nombre: string;
  marca: string;
  descripcion: string;
  categoriaId: number | null; // Cambiar a ID numérico
  unidadMedidaId: string;
  stockMinimo: number;
  diasAlertaVencimiento: number;
  proveedores: Partial<IngredienteProveedor>[];
}

const IngredienteForm: React.FC<IngredienteFormProps> = ({
  isOpen,
  onClose,
  onSave,
  ingrediente,
  modo,
}) => {
  // Obtener proveedores reales de la API
  const { data: proveedoresData, isLoading: proveedoresLoading } =
    useProveedores();

  const [formData, setFormData] = useState<FormData>({
    nombre: '',
    marca: 'genérica',
    descripcion: '',
    categoriaId: null,
    unidadMedidaId: '',
    stockMinimo: 0,
    diasAlertaVencimiento: 3,
    proveedores: [],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  // Fix tanda1 H8 (2026-06-30): el form usaba un array MOCK hardcodeado
  // de 4 unidades (id '1'..'4') que NO coincidían con las opciones
  // reales del backend. Cuando se editaba un ingrediente, su
  // `unidadMedidaId` venía como string nombre ("gramos", "mililitros")
  // del adapter y no matcheaba ningún `<option value="N">` mock → el
  // dropdown quedaba en "Seleccionar unidad" aunque la BD sí lo tenía.
  // Ahora usamos el hook real `useUnidadesMedida()`. La resolución del
  // valor pre-cargado (string legacy → id numérico) se hace en el
  // useEffect de pre-poblar más abajo.
  const { data: unidadesMaestroAPI = [] } = useUnidadesMedida();
  const unidadesMedida = (unidadesMaestroAPI as Array<{
    id: number;
    nombre: string;
    simbolo: string;
    tipo?: string;
  }>).map((u) => ({
    id: String(u.id),
    nombre: u.nombre,
    abreviacion: u.simbolo,
    tipo: (u.tipo as UnidadMedida['tipo']) || 'unidad',
    activa: true,
  })) as UnidadMedida[];

  const [proveedoresDisponibles, setProveedoresDisponibles] = useState<
    Proveedor[]
  >([]);

  // Efecto para cargar proveedores reales desde la API
  useEffect(() => {
    if (proveedoresData?.data) {
      // Mapear los proveedores de la API al formato esperado por el formulario
      const proveedoresMapeados = proveedoresData.data.map((prov: any) => ({
        id: prov.id.toString(),
        nombre: prov.nombre,
        contacto: '', // No está disponible en la API actual
        email: prov.email || '',
        telefono: prov.telefono || '',
        direccion: prov.direccion || '',
        activo: prov.activo,
      }));
      setProveedoresDisponibles(proveedoresMapeados);
    }
  }, [proveedoresData]);

  // Cargar categorías desde la API
  const { data: categoriasData, isLoading: categoriasLoading } =
    useCategorias();
  const createCategoriaMutation = useCreateCategoria();

  // Estados para agregar nueva categoría
  const [mostrarNuevaCategoria, setMostrarNuevaCategoria] = useState(false);
  const [nuevaCategoria, setNuevaCategoria] = useState('');
  const [nuevaCategoriaDescripcion, setNuevaCategoriaDescripcion] =
    useState('');

  useEffect(() => {
    if (ingrediente && modo === 'editar') {
      console.log('🔍 Inicializando formulario de edición con:', ingrediente);

      // Mapear proveedores del ingrediente al formato esperado por el formulario
      const proveedoresFormateados = (ingrediente.proveedores || []).map(
        (prov) => ({
          proveedorId: prov.proveedorId || prov.proveedor?.id || '',
          precioUnitario: prov.precioUnitario || 0,
          tiempoEntrega: prov.tiempoEntrega || 1,
          activo: prov.activo !== false,
        })
      );

      console.log('📦 Proveedores formateados:', proveedoresFormateados);

      // Fix tanda1 H8 (2026-06-30): `ingrediente.unidadMedidaId` puede
      // venir como ID numérico o como string legacy ("gramos") del
      // adapter `adaptarIngredienteDesdeAPI`. Si no matchea con ninguna
      // `<option value="<id>">`, el dropdown queda en "Seleccionar
      // unidad" pese a haber valor en BD. Resolvemos contra el
      // catálogo real por id / nombre / abreviación.
      const rawUnidad = String(ingrediente.unidadMedidaId ?? '').trim().toLowerCase();
      const matchUnidad = unidadesMedida.find((u) =>
        u.id === ingrediente.unidadMedidaId ||
        u.nombre.toLowerCase() === rawUnidad ||
        u.abreviacion.toLowerCase() === rawUnidad
      );
      const unidadIdResuelto = matchUnidad ? matchUnidad.id : ingrediente.unidadMedidaId;

      setFormData({
        nombre: ingrediente.nombre,
        marca: ingrediente.marca || 'genérica',
        descripcion: ingrediente.descripcion || '',
        categoriaId: ingrediente.categoriaId || null,
        unidadMedidaId: unidadIdResuelto,
        stockMinimo: ingrediente.stockMinimo,
        diasAlertaVencimiento: ingrediente.diasAlertaVencimiento,
        proveedores: proveedoresFormateados,
      });
    } else {
      // Reset form para modo crear
      setFormData({
        nombre: '',
        marca: 'genérica',
        descripcion: '',
        categoriaId: null,
        unidadMedidaId: '',
        stockMinimo: 0,
        diasAlertaVencimiento: 3,
        proveedores: [],
      });
    }
    setErrors({});
  }, [ingrediente, modo, isOpen]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.nombre.trim()) {
      newErrors.nombre = 'El nombre es requerido';
    }

    if (!formData.categoriaId) {
      newErrors.categoriaId = 'La categoría es requerida';
    }

    if (!formData.unidadMedidaId) {
      newErrors.unidadMedidaId = 'La unidad de medida es requerida';
    }

    if (formData.stockMinimo < 0) {
      newErrors.stockMinimo = 'El stock mínimo debe ser mayor o igual a 0';
    }

    if (formData.diasAlertaVencimiento < 1) {
      newErrors.diasAlertaVencimiento =
        'Los días de alerta deben ser mayor a 0';
    }

    // Nota: Los proveedores son opcionales, se pueden agregar después de crear el ingrediente

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Funciones para manejar nueva categoría
  const handleAgregarNuevaCategoria = async () => {
    const nombreTrim = nuevaCategoria.trim();

    if (!nombreTrim) {
      return;
    }

    try {
      const nuevaCat = await createCategoriaMutation.mutateAsync({
        nombre: nombreTrim,
        descripcion: nuevaCategoriaDescripcion.trim() || undefined,
      });

      // Seleccionar la categoría recién creada
      setFormData((prev) => ({ ...prev, categoriaId: nuevaCat.id }));

      // Limpiar y cerrar el formulario de nueva categoría
      setNuevaCategoria('');
      setNuevaCategoriaDescripcion('');
      setMostrarNuevaCategoria(false);
    } catch (error: any) {
      console.error('Error al crear categoría:', error);
      // El error se mostrará automáticamente por el manejo de errores de React Query
    }
  };

  const handleCancelarNuevaCategoria = () => {
    setNuevaCategoria('');
    setNuevaCategoriaDescripcion('');
    setMostrarNuevaCategoria(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Aquí se harían las llamadas a la API
      await new Promise((resolve) => setTimeout(resolve, 1000)); // Simular llamada API

      // Convertir proveedores a formato completo para guardar
      const unidadSeleccionada = unidadesMedida.find(
        (u) => String(u.id) === String(formData.unidadMedidaId)
      );

      const proveedoresCompletos = formData.proveedores.map((p, index) => ({
        id: `temp-${index}`, // ID temporal
        ingredienteId: ingrediente?.id || 'temp',
        proveedorId: p.proveedorId || '',
        precioUnitario: p.precioUnitario || 0,
        tiempoEntrega: p.tiempoEntrega || 1,
        activo: p.activo !== false,
        fechaUltimoPrecio: new Date(),
        proveedor: proveedoresDisponibles.find(
          (prov) => prov.id === p.proveedorId
        ) || {
          id: p.proveedorId || '',
          nombre: 'Proveedor no encontrado',
          contacto: '',
          email: '',
          telefono: '',
          direccion: '',
          activo: true,
        },
      }));

      onSave({
        ...formData,
        categoriaId: formData.categoriaId ?? undefined, // Convertir null a undefined
        id: ingrediente?.id || undefined,
        stockActual: ingrediente?.stockActual || 0,
        stockReservado: ingrediente?.stockReservado || 0,
        activo: true,
        fechaCreacion: ingrediente?.fechaCreacion || new Date(),
        fechaActualizacion: new Date(),
        proveedores: proveedoresCompletos,
        // Descriptor REAL de la unidad elegida, sacado del catalogo que este
        // formulario ya tiene cargado. Antes el adaptador lo deducia del id
        // con una tabla fija desalineada del catalogo: elegir "Gramo" (id 2)
        // terminaba guardando el ingrediente en "litros".
        unidadMedida: unidadSeleccionada
          ? {
              id: String(unidadSeleccionada.id),
              nombre: unidadSeleccionada.nombre,
              abreviacion: unidadSeleccionada.abreviacion,
              tipo: 'otro' as const,
              activa: true,
            }
          : undefined,
      });

      // Solo cerrar si no hay errores (el padre manejará el cierre si es exitoso)
      // onClose(); <- Removido para que el padre controle el cierre
    } catch (error) {
      console.error('Error al guardar ingrediente:', error);
    } finally {
      setLoading(false);
    }
  };

  const agregarProveedor = () => {
    setFormData({
      ...formData,
      proveedores: [
        ...formData.proveedores,
        {
          proveedorId: '',
          precioUnitario: 0,
          tiempoEntrega: 1,
          activo: true,
        },
      ],
    });
  };

  const actualizarProveedor = (index: number, campo: string, valor: any) => {
    const nuevosProveedores = [...formData.proveedores];
    nuevosProveedores[index] = {
      ...nuevosProveedores[index],
      [campo]: valor,
    };
    setFormData({ ...formData, proveedores: nuevosProveedores });
  };

  const eliminarProveedor = (index: number) => {
    setFormData({
      ...formData,
      proveedores: formData.proveedores.filter((_, i) => i !== index),
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            {modo === 'crear' ? 'Nuevo Ingrediente' : 'Editar Ingrediente'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Formulario */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Información básica */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                Información Básica
              </h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nombre del Ingrediente *
                </label>
                <input
                  type="text"
                  value={formData.nombre}
                  onChange={(e) =>
                    setFormData({ ...formData, nombre: e.target.value })
                  }
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100 ${
                    errors.nombre
                      ? 'border-red-500'
                      : 'border-gray-300 dark:border-gray-600'
                  }`}
                  placeholder="Ej: Pollo entero"
                />
                {errors.nombre && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                    {errors.nombre}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Marca
                </label>
                <input
                  type="text"
                  value={formData.marca}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      marca: capitalizarMarca(e.target.value),
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                  placeholder="Ej: Chef, Gallo, Colun (o genérica)"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Marca comercial del producto. Por defecto: "Genérica"
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Descripción
                </label>
                <textarea
                  value={formData.descripcion}
                  onChange={(e) =>
                    setFormData({ ...formData, descripcion: e.target.value })
                  }
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                  placeholder="Descripción adicional del ingrediente"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Categoría *
                </label>
                <div className="space-y-3">
                  <select
                    value={formData.categoriaId || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        categoriaId: e.target.value
                          ? parseInt(e.target.value)
                          : null,
                      })
                    }
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100 ${
                      errors.categoriaId
                        ? 'border-red-500'
                        : 'border-gray-300 dark:border-gray-600'
                    }`}
                    disabled={categoriasLoading}
                  >
                    <option value="">
                      {categoriasLoading
                        ? 'Cargando categorías...'
                        : 'Seleccionar categoría'}
                    </option>
                    {categoriasData?.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.nombre}
                      </option>
                    ))}
                  </select>

                  {/* Opción para agregar nueva categoría */}
                  {!mostrarNuevaCategoria ? (
                    <button
                      type="button"
                      onClick={() => setMostrarNuevaCategoria(true)}
                      className="flex items-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Agregar nueva categoría
                    </button>
                  ) : (
                    <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                      <div className="space-y-3">
                        <div>
                          <input
                            type="text"
                            value={nuevaCategoria}
                            onChange={(e) => setNuevaCategoria(e.target.value)}
                            placeholder="Nombre de la categoría *"
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAgregarNuevaCategoria();
                              }
                            }}
                          />
                        </div>
                        <div>
                          <input
                            type="text"
                            value={nuevaCategoriaDescripcion}
                            onChange={(e) =>
                              setNuevaCategoriaDescripcion(e.target.value)
                            }
                            placeholder="Descripción (opcional)"
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                          />
                        </div>
                        <div className="flex space-x-2">
                          <button
                            type="button"
                            onClick={handleAgregarNuevaCategoria}
                            disabled={
                              !nuevaCategoria.trim() ||
                              createCategoriaMutation.isPending
                            }
                            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                          >
                            {createCategoriaMutation.isPending ? (
                              <>
                                <svg
                                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  ></circle>
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                  ></path>
                                </svg>
                                Creando...
                              </>
                            ) : (
                              'Agregar'
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={handleCancelarNuevaCategoria}
                            disabled={createCategoriaMutation.isPending}
                            className="px-3 py-1.5 text-sm bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50"
                          >
                            Cancelar
                          </button>
                        </div>
                        {createCategoriaMutation.isError && (
                          <p className="text-sm text-red-600 dark:text-red-400">
                            {(createCategoriaMutation.error as any)?.response
                              ?.data?.message || 'Error al crear la categoría'}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {errors.categoriaId && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                    {errors.categoriaId}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Unidad de Medida *
                </label>
                <select
                  value={formData.unidadMedidaId}
                  onChange={(e) =>
                    setFormData({ ...formData, unidadMedidaId: e.target.value })
                  }
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100 ${
                    errors.unidadMedidaId
                      ? 'border-red-500'
                      : 'border-gray-300 dark:border-gray-600'
                  }`}
                >
                  <option value="">Seleccionar unidad</option>
                  {unidadesMedida.map((unidad) => (
                    <option key={unidad.id} value={unidad.id}>
                      {unidad.nombre} ({unidad.abreviacion})
                    </option>
                  ))}
                </select>
                {errors.unidadMedidaId && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                    {errors.unidadMedidaId}
                  </p>
                )}
                {/* La unidad fija la unidad base con la que el sistema acumula
                    stock y convierte las recetas. Editarla despues cambia solo
                    la etiqueta: el calculo sigue con la unidad original, y el
                    ingrediente queda diciendo una cosa y calculando otra. */}
                <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
                  {modo === 'crear'
                    ? 'Elegí bien la unidad: define cómo el sistema calcula el stock y las conversiones, y después no se puede corregir desde acá.'
                    : 'Cambiar la unidad acá sólo cambia la etiqueta: el stock y las conversiones se siguen calculando con la unidad original. Para corregirla de verdad, avisá al administrador.'}
                </p>
              </div>
            </div>

            {/* Configuración de stock */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                Configuración de Stock
              </h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Stock Mínimo *
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.stockMinimo}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      stockMinimo: parseFloat(e.target.value) || 0,
                    })
                  }
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100 ${
                    errors.stockMinimo
                      ? 'border-red-500'
                      : 'border-gray-300 dark:border-gray-600'
                  }`}
                  placeholder="0"
                />
                {errors.stockMinimo && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                    {errors.stockMinimo}
                  </p>
                )}
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Cantidad mínima antes de generar alerta
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Días de Alerta de Vencimiento *
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.diasAlertaVencimiento}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      diasAlertaVencimiento: parseInt(e.target.value) || 1,
                    })
                  }
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100 ${
                    errors.diasAlertaVencimiento
                      ? 'border-red-500'
                      : 'border-gray-300 dark:border-gray-600'
                  }`}
                  placeholder="3"
                />
                {errors.diasAlertaVencimiento && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                    {errors.diasAlertaVencimiento}
                  </p>
                )}
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Días antes del vencimiento para alertar
                </p>
              </div>
            </div>
          </div>

          {/* Proveedores */}
          <div className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                  Proveedores{' '}
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    (Opcional)
                  </span>
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Puedes agregar proveedores ahora o después de crear el
                  ingrediente
                </p>
              </div>
              <button
                type="button"
                onClick={agregarProveedor}
                className="inline-flex items-center px-3 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                <Plus className="h-4 w-4 mr-2" />
                Agregar Proveedor
              </button>
            </div>

            {formData.proveedores.length === 0 ? (
              <div className="text-center py-8 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
                <p className="text-gray-500 dark:text-gray-400">
                  No hay proveedores agregados.
                </p>
                <p className="text-gray-400 dark:text-gray-500 text-sm mt-1">
                  Los proveedores son opcionales y pueden agregarse después.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {formData.proveedores.map((proveedor, index) => (
                  <div
                    key={index}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Proveedor
                        </label>
                        <select
                          value={proveedor.proveedorId || ''}
                          onChange={(e) =>
                            actualizarProveedor(
                              index,
                              'proveedorId',
                              e.target.value
                            )
                          }
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                        >
                          <option value="">Seleccionar proveedor</option>
                          {proveedoresLoading ? (
                            <option disabled>Cargando proveedores...</option>
                          ) : (
                            proveedoresDisponibles.map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.nombre}
                              </option>
                            ))
                          )}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Precio Unitario ($)
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={proveedor.precioUnitario || ''}
                          onChange={(e) =>
                            actualizarProveedor(
                              index,
                              'precioUnitario',
                              parseFloat(e.target.value) || 0
                            )
                          }
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                          placeholder="0"
                        />
                      </div>

                      <div className="flex items-end">
                        <div className="flex-1 mr-2">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Tiempo Entrega (días)
                          </label>
                          <input
                            type="number"
                            min="1"
                            value={proveedor.tiempoEntrega || ''}
                            onChange={(e) =>
                              actualizarProveedor(
                                index,
                                'tiempoEntrega',
                                parseInt(e.target.value) || 1
                              )
                            }
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                            placeholder="1"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => eliminarProveedor(index)}
                          className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {errors.proveedores && (
              <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                {errors.proveedores}
              </p>
            )}
          </div>

          {/* Botones */}
          <div className="flex items-center justify-end space-x-4 mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition-colors"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {modo === 'crear' ? 'Crear Ingrediente' : 'Guardar Cambios'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default IngredienteForm;
